import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, RotateCcw, Save, Plus, X, Download, TrendingUp, TrendingDown, ArrowUpDown, Star, ShoppingBasket } from "lucide-react";
import { api } from "@/lib/api";
import { FILTER_DEFS, describeFilter } from "@/lib/filters";

const UNIVERSES = [
  { id: "bist30", label: "BIST 30", tid: "universe-btn-bist30" },
  { id: "bist100", label: "BIST 100", tid: "universe-btn-bist100" },
  { id: "bist_all", label: "Tüm BIST", tid: "universe-btn-all" },
];

export default function ScreenerPage() {
  const nav = useNavigate();
  const [universe, setUniverse] = useState("bist30");
  const [filters, setFilters] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sortKey, setSortKey] = useState("pct_change");
  const [sortDir, setSortDir] = useState("desc");
  const [presetName, setPresetName] = useState("");
  const [showPresetSave, setShowPresetSave] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showBasket, setShowBasket] = useState(false);

  // Load preset from sessionStorage (from Presets page or Saved Scans)
  React.useEffect(() => {
    const raw = sessionStorage.getItem("ztan_preset");
    if (raw) {
      try {
        const p = JSON.parse(raw);
        if (p.universe) setUniverse(p.universe);
        if (Array.isArray(p.filters)) setFilters(p.filters);
        sessionStorage.removeItem("ztan_preset");
        if (p.autorun) {
          setTimeout(() => document.querySelector('[data-testid="screener-scan-btn"]')?.click(), 250);
        }
        return;
      } catch {}
    }
    // Auto-run empty scan on first mount so the table is populated after login
    setTimeout(() => { runScanQuiet("bist30", []); }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runScanQuiet = async (uni, flt) => {
    setBusy(true);
    try {
      const { data } = await api.post("/scan", { universe: uni, filters: flt, limit: 300 });
      setResults(data);
    } catch {
      // silent — user can retry manually
    } finally { setBusy(false); }
  };

  const addFilter = (def) => {
    setFilters((prev) => [...prev, { ...def.build(), _defId: def.id }]);
    setShowAdd(false);
  };

  const removeFilter = (i) => setFilters((prev) => prev.filter((_, idx) => idx !== i));
  const resetFilters = () => { setFilters([]); toast.info("Filtreler sıfırlandı"); };

  const runScan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/scan", { universe, filters, limit: 300 });
      setResults(data);
      toast.success(`Tarama tamamlandı: ${data.match_count} hisse eşleşti`);
    } catch (e) {
      toast.error("Tarama başarısız oldu");
    } finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.post("/scan/csv", { universe, filters, limit: 500 }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "ztan_tarama.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV indirilemedi"); }
  };

  const savePreset = async () => {
    if (!presetName.trim()) return;
    try {
      await api.post("/saved-scans", { name: presetName.trim(), universe, filters });
      toast.success("Strateji kaydedildi");
      setShowPresetSave(false); setPresetName("");
    } catch { toast.error("Kaydedilemedi"); }
  };

  const toggleWatchlist = async (symbol) => {
    try {
      await api.post("/watchlist", { symbol });
      toast.success(`${symbol} izleme listesine eklendi`);
    } catch { toast.error("Eklenemedi"); }
  };

  const toggleSelect = (sym) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym); else next.add(sym);
      return next;
    });

  const sorted = React.useMemo(() => {
    if (!results?.results) return [];
    const arr = [...results.results];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [results, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const grouped = FILTER_DEFS.reduce((acc, d) => {
    (acc[d.category] = acc[d.category] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="screener-page">
      {/* Filter Builder */}
      <div className="bg-bg-panel border border-bg-border rounded-xl p-4 sm:p-5 shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-bg-border">
          <div>
            <h1 className="text-xl font-semibold text-text-primary tracking-tight ztan-title">Teknik Tarama</h1>
            <p className="text-xs text-text-secondary mt-1">BIST hisselerini indikatör kriterlerine göre tarayın</p>
          </div>
          <div className="flex items-center gap-1.5 bg-bg-base p-1 rounded-lg border border-bg-border">
            {UNIVERSES.map((u) => (
              <button
                key={u.id}
                data-testid={u.tid}
                onClick={() => setUniverse(u.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  universe === u.id ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"
                }`}
              >{u.label}</button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-4">
          {filters.length === 0 && (
            <span className="text-xs text-text-secondary italic">Kriter ekleyerek taramaya başlayın →</span>
          )}
          {filters.map((f, i) => (
            <span
              key={i}
              data-testid={`filter-badge-${i}`}
              className="bg-accent-yellow/15 text-accent-yellow border border-accent-yellow/40 px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1.5"
            >
              {describeFilter(f)}
              <button onClick={() => removeFilter(i)} className="hover:opacity-70">
                <X size={12} />
              </button>
            </span>
          ))}

          <div className="relative">
            <button
              data-testid="add-filter-btn"
              onClick={() => setShowAdd(!showAdd)}
              className="flex items-center gap-1.5 bg-bg-hover text-text-primary border border-bg-border text-xs px-3 py-1.5 rounded-md hover:bg-bg-border transition-colors"
            >
              <Plus size={13} /> Kriter Ekle
            </button>
            {showAdd && (
              <div className="absolute left-0 mt-2 w-80 bg-bg-panel border border-bg-border rounded-xl shadow-2xl z-30 p-3 max-h-96 overflow-y-auto">
                {Object.entries(grouped).map(([cat, defs]) => (
                  <div key={cat} className="mb-3">
                    <div className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold px-2 mb-1">{cat}</div>
                    {defs.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => addFilter(d)}
                        className="w-full text-left text-xs text-text-primary px-2 py-1.5 hover:bg-bg-hover rounded transition-colors"
                      >{d.label}</button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-5">
          <button
            data-testid="screener-scan-btn"
            onClick={runScan}
            disabled={busy}
            className="bg-accent-yellow text-bg-base font-bold px-6 py-2.5 rounded-lg hover:opacity-95 active:scale-[0.98] flex items-center gap-2 shadow-md disabled:opacity-60 text-sm"
          >
            <Play size={15} /> {busy ? "Taranıyor..." : "Tara"}
          </button>
          <button
            data-testid="screener-save-preset-btn"
            onClick={() => setShowPresetSave(true)}
            className="bg-bg-hover text-text-primary border border-bg-border px-4 py-2.5 rounded-lg hover:bg-bg-border text-sm flex items-center gap-2"
          ><Save size={14} /> Stratejiyi Kaydet</button>
          <button
            data-testid="screener-reset-btn"
            onClick={resetFilters}
            className="text-text-secondary hover:text-text-primary text-xs flex items-center gap-1.5"
          ><RotateCcw size={12} /> Sıfırla</button>
        </div>

        {showPresetSave && (
          <div className="mt-3 flex gap-2 items-center bg-bg-base border border-bg-border p-3 rounded-lg">
            <input
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Strateji adı..."
              className="flex-1 bg-bg-panel border border-bg-border text-text-primary text-sm rounded-md px-3 py-2 focus:border-brand-blue focus:outline-none"
            />
            <button onClick={savePreset} className="bg-brand-blue text-white px-4 py-2 text-sm rounded-md">Kaydet</button>
            <button onClick={() => setShowPresetSave(false)} className="text-text-secondary text-sm px-2">İptal</button>
          </div>
        )}
      </div>

      {/* Results */}
      {busy && <LoadingState />}

      {!busy && results && (
        <div className="bg-bg-panel border border-bg-border rounded-xl overflow-hidden shadow-xl">
          <div className="p-4 border-b border-bg-border flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-text-secondary">
              <span className="text-accent-yellow font-semibold" data-testid="match-count">{results.match_count}</span> hisse eşleşti
              <span className="text-text-secondary/60 mx-2">•</span>
              <span>{results.total_scanned} taranıldı</span>
            </div>
            <button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              className="bg-bg-base border border-bg-border text-text-primary hover:bg-bg-hover text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5"
            ><Download size={12} /> CSV İndir</button>
          </div>

          {/* Basket action bar */}
          {selected.size > 0 && (
            <div data-testid="basket-bar" className="px-4 py-3 border-b border-bg-border bg-bg-hover flex items-center justify-between gap-3">
              <div className="text-sm text-text-primary flex items-center gap-2">
                <ShoppingBasket size={15} className="text-accent-yellow" />
                <span className="font-medium">{selected.size}</span> hisse seçili
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setSelected(new Set())} className="text-text-secondary hover:text-text-primary text-xs px-2">Temizle</button>
                <button
                  data-testid="basket-buy-btn"
                  onClick={() => setShowBasket(true)}
                  className="bg-accent-yellow text-bg-base font-semibold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 hover:opacity-90"
                ><ShoppingBasket size={12} /> Sepete Ekle & Al</button>
              </div>
            </div>
          )}

          {sorted.length === 0 ? (
            <EmptyState onReset={resetFilters} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-text-secondary text-xs uppercase tracking-wider border-b border-bg-border">
                    <th className="py-3 px-2 w-8"></th>
                    <Th onClick={() => setSort("symbol")} sortKey={sortKey} me="symbol" dir={sortDir}>Hisse</Th>
                    <Th onClick={() => setSort("close")} sortKey={sortKey} me="close" dir={sortDir} align="right">Son Fiyat (TL)</Th>
                    <Th onClick={() => setSort("pct_change")} sortKey={sortKey} me="pct_change" dir={sortDir} align="right">% Değişim</Th>
                    <Th onClick={() => setSort("rsi")} sortKey={sortKey} me="rsi" dir={sortDir} align="right">RSI (14)</Th>
                    <Th align="center">MACD</Th>
                    <Th align="center">EMA 20/50</Th>
                    <Th onClick={() => setSort("volume_ratio")} sortKey={sortKey} me="volume_ratio" dir={sortDir} align="right">Hacim Katı</Th>
                    <Th align="center">Bollinger</Th>
                    <Th align="center">İşlemler</Th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr
                      key={r.symbol}
                      data-testid={`stock-row-${r.symbol}`}
                      onClick={() => nav(`/stock/${r.symbol}`)}
                      className="border-b border-bg-border hover:bg-bg-hover transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          data-testid={`basket-check-${r.symbol}`}
                          type="checkbox"
                          checked={selected.has(r.symbol)}
                          onChange={() => toggleSelect(r.symbol)}
                          className="w-4 h-4 accent-accent-yellow cursor-pointer"
                        />
                      </td>
                      <td className="py-3 px-4 font-mono font-bold text-text-primary">{r.symbol}
                        <div className="text-[10px] text-text-secondary font-sans font-normal mt-0.5">{r.name}</div>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-text-primary">{fmtNum(r.close)}</td>
                      <td className={`py-3 px-4 text-right font-mono font-medium ${signalClass(r.pct_change)}`}>
                        {fmtPct(r.pct_change)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-text-primary">{fmtNum(r.rsi, 1)}</td>
                      <td className="py-3 px-4 text-center">{StatusPill(r.macd_status)}</td>
                      <td className="py-3 px-4 text-center">{StatusPill(r.ma_status)}</td>
                      <td className="py-3 px-4 text-right font-mono text-text-primary">{r.volume_ratio ? `${r.volume_ratio.toFixed(2)}x` : "-"}</td>
                      <td className="py-3 px-4 text-center">{BbPill(r.bb_status)}</td>
                      <td className="py-3 px-4 text-center">
                        <button
                          data-testid={`stock-favorite-btn-${r.symbol}`}
                          onClick={(e) => { e.stopPropagation(); toggleWatchlist(r.symbol); }}
                          className="text-text-secondary hover:text-accent-yellow transition-colors"
                        ><Star size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!busy && !results && <InitialState />}

      {showBasket && (
        <BasketModal
          symbols={Array.from(selected)}
          onClose={() => setShowBasket(false)}
          onDone={() => { setShowBasket(false); setSelected(new Set()); }}
        />
      )}
    </div>
  );
}

function BasketModal({ symbols, onClose, onDone }) {
  const [amount, setAmount] = useState(10000);
  const [busy, setBusy] = useState(false);
  const perSymbol = symbols.length ? amount / symbols.length : 0;

  const submit = async () => {
    if (!symbols.length || amount <= 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/portfolio/basket", { symbols, total_amount: Number(amount), mode: "equal_weight" });
      toast.success(`${data.count} hisse alındı — ₺${Number(amount).toLocaleString("tr-TR")} toplam`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sepet alımı başarısız");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="basket-modal" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-bg-panel border border-bg-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-bg-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <ShoppingBasket size={16} className="text-accent-yellow" /> Sepet Alımı
          </h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-text-secondary">
            {symbols.length} hisse eşit ağırlıkla alınacak — kağıt portföy simülasyonu
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
            {symbols.map((s) => (
              <span key={s} className="text-[10px] font-mono px-2 py-0.5 rounded border bg-bg-base border-bg-border text-text-primary">{s}</span>
            ))}
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">Toplam Tutar (₺)</label>
            <input
              data-testid="basket-amount-input"
              type="number" min="100" step="100"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-bg-base border border-bg-border text-text-primary font-mono rounded-lg px-3 py-2.5 text-sm focus:border-brand-blue focus:outline-none"
            />
          </div>
          <div className="bg-bg-base border border-bg-border rounded-lg p-3 text-xs">
            <div className="flex justify-between text-text-secondary"><span>Hisse başına ayrılan</span><span className="text-text-primary font-mono">≈ ₺{Number(perSymbol || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between mt-1 text-text-secondary"><span>Toplam yaklaşık</span><span className="text-accent-yellow font-mono">₺{Number(amount || 0).toLocaleString("tr-TR")}</span></div>
          </div>
          <button
            data-testid="basket-submit-btn"
            onClick={submit}
            disabled={busy || symbols.length === 0 || amount <= 0}
            className="w-full bg-signal-up text-white font-bold py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 text-sm"
          >{busy ? "İşleniyor..." : "Sepeti Onayla"}</button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align = "left", onClick, sortKey, me, dir }) {
  const active = sortKey && me && sortKey === me;
  return (
    <th
      onClick={onClick}
      className={`py-3 px-4 font-medium select-none ${onClick ? "cursor-pointer" : ""} ${
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"
      } ${active ? "text-brand-blue" : ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {onClick && <ArrowUpDown size={11} className="opacity-60" />}
      </span>
    </th>
  );
}

function fmtNum(v, dec = 2) {
  if (v == null) return "-";
  return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtPct(v) {
  if (v == null) return "-";
  const s = Number(v).toFixed(2);
  return `${v > 0 ? "+" : ""}${s}%`;
}
function signalClass(v) {
  if (v == null) return "text-text-secondary";
  return v >= 0 ? "text-signal-up" : "text-signal-down";
}

function StatusPill(status) {
  if (!status) return <span className="text-text-secondary text-xs">-</span>;
  const map = {
    golden_cross: { l: "Altın Kes.", c: "text-signal-up bg-signal-up/10 border-signal-up/30", i: TrendingUp },
    bullish: { l: "Yükseliş", c: "text-signal-up bg-signal-up/10 border-signal-up/30", i: TrendingUp },
    death_cross: { l: "Ölüm Kes.", c: "text-signal-down bg-signal-down/10 border-signal-down/30", i: TrendingDown },
    bearish: { l: "Düşüş", c: "text-signal-down bg-signal-down/10 border-signal-down/30", i: TrendingDown },
  };
  const s = map[status] || { l: status, c: "text-text-secondary", i: TrendingUp };
  const Icon = s.i;
  return <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border ${s.c}`}><Icon size={10} />{s.l}</span>;
}
function BbPill(status) {
  if (!status) return <span className="text-text-secondary text-xs">-</span>;
  if (status === "upper_break") return <span className="text-[10px] px-2 py-0.5 rounded border text-signal-up bg-signal-up/10 border-signal-up/30">Üst Kırılım</span>;
  if (status === "lower_break") return <span className="text-[10px] px-2 py-0.5 rounded border text-signal-down bg-signal-down/10 border-signal-down/30">Alt Kırılım</span>;
  return <span className="text-[10px] px-2 py-0.5 rounded border text-text-secondary border-bg-border">Bantiçi</span>;
}

function LoadingState() {
  return (
    <div className="bg-bg-panel border border-bg-border rounded-xl p-16 flex flex-col items-center gap-4">
      <div className="radar-loader" />
      <div className="text-sm text-text-secondary">BIST hisseleri taranıyor...</div>
    </div>
  );
}
function EmptyState({ onReset }) {
  return (
    <div className="p-16 flex flex-col items-center gap-3 text-center">
      <div className="w-14 h-14 rounded-full bg-bg-hover flex items-center justify-center border border-bg-border">
        <Play size={20} className="text-text-secondary" />
      </div>
      <h3 className="text-text-primary font-semibold">Kriterlere Uygun Hisse Bulunamadı</h3>
      <p className="text-text-secondary text-sm max-w-md">Seçtiğiniz indikatör filtreleri çok dar olabilir. Filtre eşiklerini gevşetebilir veya farklı bir strateji deneyebilirsiniz.</p>
      <button onClick={onReset} className="mt-2 bg-accent-yellow text-bg-base font-semibold px-4 py-2 rounded-lg text-sm">Filtreleri Sıfırla</button>
    </div>
  );
}
function InitialState() {
  return (
    <div className="bg-bg-panel border border-bg-border rounded-xl p-16 flex flex-col items-center gap-3 text-center">
      <div className="w-14 h-14 rounded-full bg-brand-blue/10 border border-brand-blue/30 flex items-center justify-center">
        <Play size={22} className="text-brand-blue" />
      </div>
      <h3 className="text-text-primary text-lg font-semibold">Taramaya Hazır</h3>
      <p className="text-text-secondary text-sm max-w-md">Yukarıdan kriterlerinizi seçin ve <span className="text-accent-yellow font-medium">Tara</span> butonuna basın. Kriter eklemeden de tüm evreni tarayabilirsiniz.</p>
    </div>
  );
}
